import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';

import { useOptionalAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';

import { useOptionalDocumentChatContext } from '../contexts/DocumentChatProvider';
import { DocumentChatDrawer } from './DocumentChatDrawer';
import { DocumentChatPanel } from './DocumentChatPanel';

export interface DocumentChatPaneProps {
  enableCopyCitationPopover?: boolean;
  documentId?: string;
}

export function DocumentChatPane({
  documentId,
  enableCopyCitationPopover = false,
}: DocumentChatPaneProps) {
  const chatContext = useOptionalDocumentChatContext();
  const pdfViewerContext = useOptionalAnnotatedPDFViewerContext();

  useEffect(() => {
    chatContext?.setDocumentId(documentId);
    chatContext?.clearChat();
    pdfViewerContext?.setAnnotations(undefined);

    // Only depend on the resolved document id to avoid resetting the chat
    // on every stable context function reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chat context methods are stable and intentionally excluded
  }, [documentId]);

  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('xl'));

  const isAIEnabled = useAICapabilitiesEnabled();
  const isDocumentChatEnabled = !!chatContext && isAIEnabled;

  if (isDocumentChatEnabled && chatContext?.isChatOpen) {
    if (isMediumScreen) {
      return (
        <DocumentChatDrawer
          enableCopyCitationPopover={enableCopyCitationPopover}
        />
      );
    }

    return (
      <DocumentChatPanel
        enableCopyCitationPopover={enableCopyCitationPopover}
      />
    );
  }

  return null;
}
